import { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { useNewGivingUI } from "Flocknote/apiHooks";
import { navSectionKeys } from "NavBar/navBarStore";

import FnrIcon from "UI/FnrIcon";
import FnrSlides from "UI/FnrSlides";
import { navAccentColors } from "UI/colors";

import useOnClickOutside from "Utilities/Hooks/useOnClickOutside";

import SettingsContent from "./SettingsContent";

const slideComponents = [
  { key: "nothing", value: () => null },
  { key: "content", value: SettingsContent },
];

const givingBgColor = navAccentColors[navSectionKeys.giving]?.[1];

const GivingHeaderBadge = () => {
  const { hideLegacyGivingUI } = useNewGivingUI();
  const [showContent, setShowContent] = useState(false);
  const [closeTrigger, setCloseTrigger] = useState({});
  const containerRef = useRef();

  const handleClickOutside = useCallback(() => setShowContent(false), []);
  useOnClickOutside(containerRef, handleClickOutside);

  useEffect(() => {
    setShowContent(false);
  }, [closeTrigger]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    setShowContent((prev) => !prev);
  }, []);

  return (
    <Container ref={containerRef}>
      <Badge
        className={hideLegacyGivingUI ? "new-giving-badge" : "classic-giving-badge"}
        $background="hsl(0 0% 30% / .3)"
        $color={hideLegacyGivingUI ? "cornsilk" : "white"}
        onClick={handleClick}
        type="button"
        data-cy="GivingHeaderBadge"
      >
        {hideLegacyGivingUI ? (
          <>
            New
            <FnrIcon
              icon="fa-sparkles"
              fas
              style={{
                position: "absolute",
                top: "-6px",
                right: "-6px",
                fontSize: "0.7rem",
                color: "cornsilk",
              }}
            />
          </>
        ) : (
          "Classic"
        )}
      </Badge>
      <Background $showContent={showContent}>
        <FnrSlides
          variant="fade"
          activeKey={showContent ? "content" : "nothing"}
          components={slideComponents}
          setCloseTrigger={setCloseTrigger}
          hideAdminOptions
        />
      </Background>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const Badge = styled.button`
  position: relative;
  margin-left: -16px;
  margin-top: 2px;
  padding: 2px 6px;
  border-radius: 6px;
  transition: background 300ms, filter 200ms;
  font-size: 0.625rem;
  font-weight: bold;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  box-shadow: 0 0 4px 1px #fff8dc88;
  cursor: pointer;
  border: none;
  font-family: inherit;
  display: inline-flex;
  align-items: center;

  &:hover {
    filter: brightness(1.2);
  }
`;

const Background = styled.div`
  position: fixed;
  top: auto;
  z-index: 20;
  border-radius: 11px;
  background-color: ${givingBgColor};
  padding: ${({ $showContent }) => ($showContent ? "4px" : "0px")};
  transition: padding 300ms ease-in-out, border-radius 300ms ease-in-out,
    background-color 300ms ease-in-out;
`;

export default GivingHeaderBadge;
